import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QueenStageHints } from '../components/queen/QueenStageHints';
import { StageActions } from '../components/queen/StageActions';
import { buildGraftCalendar } from '../utils/graftCalendarPdf';

const printGraftCalendar = (list) => {
  const dated = list.filter(b => b.graft_date);
  if (dated.length === 0) { toast.error('Add a graft date to print a calendar'); return; }
  try {
    const name = dated.length === 1 ? (dated[0].batch_name || `batch-${dated[0].batch_number || ''}`).replace(/[^a-z0-9]+/gi, '-').toLowerCase() : 'all-batches';
    buildGraftCalendar(dated).save(`graft-calendar-${name}.pdf`);
    toast.success(dated.length === 1 ? 'Graft calendar downloaded' : `Graft calendar downloaded (${dated.length} pages)`);
  } catch {
    toast.error('Failed to create calendar');
  }
};
import { QueenReminderAlerts } from '../components/queen/QueenReminderAlerts';
import { QueenReminderSettings } from '../components/queen/QueenReminderSettings';
import { CellHivePicker, useCandidateHives, candidateLabel } from '../components/queen/CellHivePicker';
import { TransferTargetToggle } from '../components/queen/TransferTargetToggle';
import { ArrowLeft, Plus, Crown, Calendar, Clock, CheckCircle2, AlertCircle, Egg, Home, Heart, X, Edit2, Trash2, ChevronDown, ChevronUp, Download, FileText, Archive, RotateCcw, MapPin, Bell, Settings, ShoppingBag, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageTourButton from '../components/PageTourButton';
import { Textarea } from '../components/ui/textarea';
import { Toggle } from '../components/ui/toggle';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL || '';

const QueenRearing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  
  // Check URL for tab parameter
  const initialTab = searchParams.get('batch') ? 'batches' : (searchParams.get('tab') || 'breeders');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [focusBatchId, setFocusBatchId] = useState(searchParams.get('batch'));
  const [showReminderSettings, setShowReminderSettings] = useState(false);
  
  const [batches, setBatches] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [matingNucs, setMatingNucs] = useState([]);
  const [hives, setHives] = useState([]);
  const [yards, setYards] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYard, setFilterYard] = useState('all');

  // Apiary enrichment + filtering
  const hiveYard = (id) => (id && hives.find(h => h.id === id)?.yard) || '';
  const yardIdToName = (id) => (id && yards.find(y => y.id === id)?.name) || '';

  const enrichedBatches = useMemo(() => batches.map(b => ({
    ...b,
    yard_name: b.yard_name || hiveYard(b.cell_builder_hive_id) || hiveYard(b.breeder_hive_id) || hiveYard(b.finisher_hive_id)
  })), [batches, hives]);

  const enrichedBreeders = useMemo(() => breeders.map(b => ({
    ...b, yard_name: b.yard_name || hiveYard(b.source_hive_id)
  })), [breeders, hives]);

  const enrichedNucs = useMemo(() => matingNucs.map(n => ({
    ...n, yard_name: n.yard_name || yardIdToName(n.yard_id) || n.location || ''
  })), [matingNucs, yards]);

  const filteredBatches = useMemo(() => filterYard === 'all' ? enrichedBatches : enrichedBatches.filter(b => b.yard_name === filterYard), [enrichedBatches, filterYard]);
  const filteredBreeders = useMemo(() => filterYard === 'all' ? enrichedBreeders : enrichedBreeders.filter(b => b.yard_name === filterYard), [enrichedBreeders, filterYard]);
  const filteredNucs = useMemo(() => filterYard === 'all' ? enrichedNucs : enrichedNucs.filter(n => n.yard_name === filterYard), [enrichedNucs, filterYard]);
  const saleReadyBatches = useMemo(() => filteredBatches
    .filter(b => b.status !== 'archived' && b.graft_date && !b.listed_for_sale)
    .map(b => ({
      ...b,
      day: Math.floor((Date.now() - new Date(b.graft_date).getTime()) / 86400000),
      laying: (b.cells || []).filter(c => c.status === 'laying').length || b.queens_laying || 0
    }))
    .filter(b => b.day >= 30 && b.day <= 60), [filteredBatches]);
  const formHives = (yardName) => yardName ? hives.filter(h => h.yard === yardName) : hives;
  const hiveLabel = (h) => `Hive ${h.hive_number}${h.yard ? ` · ${h.yard}` : ''}`;

  // Dialogs
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showBreederDialog, setShowBreederDialog] = useState(false);
  const [showNucDialog, setShowNucDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showMoveToHiveDialog, setShowMoveToHiveDialog] = useState(false);
  const [showBulkTransferDialog, setShowBulkTransferDialog] = useState(false);
  const [showArchivedBatches, setShowArchivedBatches] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editingBreeder, setEditingBreeder] = useState(null);
  const [editingNuc, setEditingNuc] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [transferringCell, setTransferringCell] = useState(null);
  const [transferTarget, setTransferTarget] = useState('nuc');
  const [bulkTarget, setBulkTarget] = useState('nuc');
  const [bulkHiveMap, setBulkHiveMap] = useState({});
  const [transferBusy, setTransferBusy] = useState(false);
  const bulkCandidates = useCandidateHives(showBulkTransferDialog && bulkTarget === 'hive');
  const [movingNuc, setMovingNuc] = useState(null); // NUC with laying queen to move to hive
  const [selectedCellsForBulk, setSelectedCellsForBulk] = useState([]); // For bulk transfer
  
  // Form data
  const [batchForm, setBatchForm] = useState({
    batch_name: '',
    graft_date: '',
    breeder_queen_id: '',
    breeder_queen_name: '',
    breeder_hive_id: '',
    breeder_hive_number: '',
    yard_id: '',
    yard_name: '',
    cell_starter_hive_id: '',
    cell_starter_hive_number: '',
    cell_builder_hive_id: '',
    cell_builder_hive_number: '',
    cell_builder_method: 'queenless',
    finisher_hive_id: '',
    finisher_hive_number: '',
    cells_grafted: 10,
    cells_per_frame: 15,
    grafting_tool: 'chinese',
    cell_cups: 'plastic',
    incubator_used: false,
    notes: ''
  });
  
  const [breederForm, setBreederForm] = useState({
    name: '',
    source_hive_id: '',
    source_hive_number: '',
    yard_id: '',
    yard_name: '',
    genetics: '',
    traits: [],
    acquired_date: '',
    source: '',
    notes: '',
    status: 'active'
  });
  
  const [nucForm, setNucForm] = useState({
    nuc_number: '',
    nuc_type: 'apidea',
    location: '',
    yard_id: '',
    yard_name: '',
    status: 'available',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Coming from a queenless alert: open Quick Transfer in hive mode with that hive ready
  const [alertHiveId, setAlertHiveId] = useState(null);
  useEffect(() => {
    const id = searchParams.get('transfer_to_hive');
    if (!id) return;
    setAlertHiveId(id);
    setBulkTarget('hive');
    setShowBulkTransferDialog(true);
    const next = new URLSearchParams(searchParams);
    next.delete('transfer_to_hive');
    setSearchParams(next, { replace: true });
  }, [searchParams]);

  const wasBulkOpen = React.useRef(false);
  useEffect(() => {
    if (wasBulkOpen.current && !showBulkTransferDialog) setAlertHiveId(null);
    wasBulkOpen.current = showBulkTransferDialog;
  }, [showBulkTransferDialog]);

  useEffect(() => {
    if (!token) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    fetch(`${API}/api/notification-settings`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(settings => {
        if (tz && settings && !settings.timezone) {
          fetch(`${API}/api/users/me/timezone`, { method: 'POST', headers, body: JSON.stringify({ timezone: tz }) });
        }
      });
  }, [token]);

  const openBatchCells = (batchId) => {
    setActiveTab('batches');
    setFocusBatchId(batchId);
  };

  const listQueensForSale = (batchId) => navigate(`/?view=marketplace&listQueens=${batchId}`);

  const handleBulkCellStatus = async (batchId, status, count) => {
    try {
      const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells/bulk-status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, count })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to update cells');
    }
  };

  const openTransferForBatch = (batch) => {
    const label = batch.batch_name || `Batch #${batch.batch_number}`;
    setSelectedCellsForBulk((batch.cells || []).filter(c => c.status === 'capped').map(c => ({ cellId: c.id, cellNumber: c.cell_number, batchId: batch.id, batchName: label })));
    setShowBulkTransferDialog(true);
  };

  const fetchData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [batchesRes, breedersRes, nucsRes, hivesRes, yardsRes, calendarRes, inspectionsRes] = await Promise.all([
        fetch(`${API}/api/queen-rearing/batches`, { headers }),
        fetch(`${API}/api/queen-rearing/breeders`, { headers }),
        fetch(`${API}/api/queen-rearing/mating-nucs`, { headers }),
        fetch(`${API}/api/hives`, { headers }),
        fetch(`${API}/api/yards`, { headers }),
        fetch(`${API}/api/queen-rearing/calendar`, { headers }),
        fetch(`${API}/api/inspections`, { headers })
      ]);
      
      if (batchesRes.ok) setBatches(await batchesRes.json());
      if (breedersRes.ok) setBreeders(await breedersRes.json());
      if (nucsRes.ok) setMatingNucs(await nucsRes.json());
      if (hivesRes.ok) setHives(await hivesRes.json());
      if (yardsRes.ok) setYards(await yardsRes.json());
      if (calendarRes.ok) setCalendarEvents(await calendarRes.json());
      if (inspectionsRes.ok) {
        const inspData = await inspectionsRes.json();
        setInspections(inspData);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      const batches = filteredBatches, breeders = filteredBreeders, matingNucs = filteredNucs;
      let csv = '';
      
      // Header
      csv += '"QUEEN REARING REPORT"\n';
      csv += `"Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}"\n`;
      csv += `"Apiary","${filterYard === 'all' ? 'All Apiaries' : filterYard}"\n\n`;
      
      // Summary Stats
      csv += '"SUMMARY"\n';
      csv += `"Active Batches","${batches.filter(b => b.status !== 'complete' && b.status !== 'archived').length}"\n`;
      csv += `"Total Breeder Queens","${breeders.length}"\n`;
      csv += `"Active Breeders","${breeders.filter(b => b.status === 'active').length}"\n`;
      csv += `"Mating Nucs","${matingNucs.length}"\n`;
      csv += `"Total Cells Grafted","${batches.reduce((sum, b) => sum + (b.cells_grafted || 0), 0)}"\n`;
      csv += `"Queens Laying","${batches.reduce((sum, b) => sum + (b.queens_laying || 0), 0)}"\n\n`;
      
      // Breeder Queens
      csv += '"BREEDER QUEENS"\n';
      csv += '"Name","Apiary","Genetics","Status","Source Hive","Acquired Date","Notes"\n';
      breeders.forEach(b => {
        csv += `"${b.name || ''}","${b.yard_name || ''}","${b.genetics || ''}","${b.status || ''}","${b.source_hive_number ? 'Hive #' + b.source_hive_number : ''}","${b.acquired_date || ''}","${(b.notes || '').replace(/"/g, '""')}"\n`;
      });
      csv += '\n';
      
      // Batches
      csv += '"BATCHES"\n';
      csv += '"Batch Name","Apiary","Graft Date","Breeder Queen","Cell Starter Hive","Finisher Hive","Cells Grafted","Cells Capped","Queens Emerged","Queens Mated","Queens Laying","Status","Grafting Tool","Cell Cups","Notes"\n';
      batches.forEach(b => {
        csv += `"${b.batch_name || 'Batch #' + b.batch_number}","${b.yard_name || ''}","${b.graft_date || ''}","${b.breeder_queen_name || ''}","${b.cell_builder_hive_number ? 'Hive #' + b.cell_builder_hive_number : ''}","${b.finisher_hive_number ? 'Hive #' + b.finisher_hive_number : (b.cell_builder_hive_number ? 'Same as starter' : '')}","${b.cells_grafted || 0}","${b.cells_capped || 0}","${b.queens_emerged || 0}","${b.queens_mated || 0}","${b.queens_laying || 0}","${b.status || ''}","${b.grafting_tool || ''}","${b.cell_cups || ''}","${(b.notes || '').replace(/"/g, '""')}"\n`;
      });
      csv += '\n';
      
      // Mating Nucs
      csv += '"MATING NUCS"\n';
      csv += '"Nuc Number","Apiary","Type","Location","Status","Queen ID","Notes"\n';
      matingNucs.forEach(n => {
        csv += `"${n.nuc_number || ''}","${n.yard_name || ''}","${n.nuc_type || ''}","${n.location || ''}","${n.status || ''}","${n.queen_id || ''}","${(n.notes || '').replace(/"/g, '""')}"\n`;
      });
      csv += '\n';
      
      // Timeline Events
      csv += '"TIMELINE EVENTS"\n';
      csv += '"Date","Event","Batch","Status"\n';
      calendarEvents.forEach(e => {
        csv += `"${e.date || ''}","${e.title || ''}","${e.batch_name || ''}","${e.completed ? 'Completed' : 'Pending'}"\n`;
      });
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `queen-rearing-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const batches = filteredBatches, breeders = filteredBreeders, matingNucs = filteredNucs;
      const doc = new jsPDF();
      let yPos = 20;
      const pageWidth = 210;
      const marginLeft = 14;
      const contentWidth = 182;
      
      // Helper function to capitalize first letter of each word
      const toTitleCase = (str) => {
        if (!str) return '-';
        return str.replace(/\b\w/g, l => l.toUpperCase());
      };
      
      // Helper function to format date
      const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      };
      
      // Helper to calculate timeline dates from graft date
      const getTimelineDates = (graftDate) => {
        if (!graftDate) return null;
        const graft = new Date(graftDate);
        return {
          graft: graft,
          capped: new Date(graft.getTime() + 9 * 24 * 60 * 60 * 1000),
          transfer: new Date(graft.getTime() + 10 * 24 * 60 * 60 * 1000),
          emerge: new Date(graft.getTime() + 16 * 24 * 60 * 60 * 1000),
          mating: new Date(graft.getTime() + 22 * 24 * 60 * 60 * 1000),
          check: new Date(graft.getTime() + 30 * 24 * 60 * 60 * 1000)
        };
      };
      
      // Row height for better vertical alignment
      const rowHeight = 8;
      const headerHeight = 9;
      
      // Header
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 83, 9);
      doc.text('Queen Rearing Report', marginLeft, yPos);
      yPos += 12;
      
      // Date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} at ${new Date().toLocaleTimeString()}`, marginLeft, yPos);
      yPos += 6;
      doc.text(`Apiary: ${filterYard === 'all' ? 'All Apiaries' : filterYard}`, marginLeft, yPos);
      yPos += 12;
      
      // Summary Stats Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text('Summary Statistics', marginLeft, yPos);
      yPos += 10;
      
      // Draw stat boxes
      const stats = [
        { label: 'Active Batches', value: batches.filter(b => b.status !== 'complete' && b.status !== 'archived').length, color: [254, 243, 199] },
        { label: 'Breeder Queens', value: breeders.length, color: [243, 232, 255] },
        { label: 'Mating Nucs', value: matingNucs.length, color: [219, 234, 254] },
        { label: 'Total Cells', value: batches.reduce((sum, b) => sum + (b.cells_grafted || 0), 0), color: [254, 226, 226] }
      ];
      
      stats.forEach((stat, i) => {
        const x = marginLeft + (i * 46);
        doc.setFillColor(...stat.color);
        doc.roundedRect(x, yPos, 44, 22, 2, 2, 'F');
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text(String(stat.value), x + 22, yPos + 12, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.text(stat.label, x + 22, yPos + 18, { align: 'center' });
      });
      yPos += 32;
      
      // Breeder Queens Table
      if (breeders.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(147, 51, 234);
        doc.text('Breeder Queens', marginLeft, yPos);
        yPos += 8;
        
        // Table header
        doc.setFillColor(147, 51, 234);
        doc.rect(marginLeft, yPos, contentWidth, headerHeight, 'F');
        doc.setTextColor(255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const headerY = yPos + 6;
        doc.text('Name', marginLeft + 4, headerY);
        doc.text('Apiary', marginLeft + 50, headerY);
        doc.text('Genetics', marginLeft + 92, headerY);
        doc.text('Status', marginLeft + 127, headerY);
        doc.text('Source Hive', marginLeft + 152, headerY);
        yPos += headerHeight + 1;
        
        // Table rows
        doc.setFont('helvetica', 'normal');
        breeders.forEach((b, i) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          if (i % 2 === 0) {
            doc.setFillColor(250, 245, 255);
            doc.rect(marginLeft, yPos - 1, contentWidth, rowHeight, 'F');
          }
          
          doc.setTextColor(0);
          doc.setFontSize(9);
          const textY = yPos + 5;
          doc.text(toTitleCase(b.name || '-').substring(0, 24), marginLeft + 4, textY);
          doc.text((b.yard_name || '-').substring(0, 20), marginLeft + 50, textY);
          doc.text(toTitleCase(b.genetics || '-').substring(0, 16), marginLeft + 92, textY);
          doc.text(toTitleCase(b.status || '-'), marginLeft + 127, textY);
          doc.text(b.source_hive_number ? `Hive #${b.source_hive_number}` : '-', marginLeft + 152, textY);
          yPos += rowHeight;
        });
        yPos += 10;
      }
      
      // Batches with Timeline
      if (batches.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(180, 83, 9);
        doc.text('Batches', marginLeft, yPos);
        yPos += 8;
        
        // Table header
        doc.setFillColor(180, 83, 9);
        doc.rect(marginLeft, yPos, contentWidth, headerHeight, 'F');
        doc.setTextColor(255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const headerY = yPos + 6;
        doc.text('Batch Name', marginLeft + 4, headerY);
        doc.text('Breeder Queen', marginLeft + 55, headerY);
        doc.text('Cell Starter', marginLeft + 105, headerY);
        doc.text('Cells', marginLeft + 150, headerY);
        doc.text('Status', marginLeft + 168, headerY);
        yPos += headerHeight + 1;
        
        // Batch rows
        doc.setFont('helvetica', 'normal');
        batches.forEach((b, i) => {
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          
          // Batch row
          if (i % 2 === 0) {
            doc.setFillColor(255, 251, 235);
            doc.rect(marginLeft, yPos - 1, contentWidth, rowHeight, 'F');
          }
          
          doc.setTextColor(0);
          doc.setFontSize(9);
          const textY = yPos + 5;
          doc.text((b.batch_name || `Batch #${b.batch_number || '?'}`).substring(0, 22), marginLeft + 4, textY);
          doc.text(toTitleCase(b.breeder_queen_name || '-').substring(0, 20), marginLeft + 55, textY);
          doc.text(b.cell_builder_hive_number ? `Hive #${b.cell_builder_hive_number}` : '-', marginLeft + 105, textY);
          doc.text(String(b.cells_grafted || 0), marginLeft + 150, textY);
          doc.text(toTitleCase(b.status || '-'), marginLeft + 168, textY);
          yPos += rowHeight;
          doc.setFontSize(7.5);
          doc.setTextColor(90);
          const finisher = b.finisher_hive_number ? `Hive #${b.finisher_hive_number}` : 'Same as starter';
          doc.text(`Apiary: ${b.yard_name || '-'}   |   Finisher: ${finisher}   |   Graft: ${formatDate(b.graft_date)}   |   Capped: ${b.cells_capped || 0}  Emerged: ${b.queens_emerged || 0}  Mated: ${b.queens_mated || 0}  Laying: ${b.queens_laying || 0}`.substring(0, 140), marginLeft + 4, yPos + 4);
          yPos += 6;
          
          // Timeline for this batch - displayed as a mini table
          const timeline = getTimelineDates(b.graft_date);
          if (timeline) {
            // Timeline header row
            doc.setFillColor(254, 243, 199);
            doc.rect(marginLeft + 4, yPos, contentWidth - 4, 8, 'F');
            
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(120, 53, 0);
            
            // Draw 6 column headers for timeline milestones
            const colWidth = (contentWidth - 4) / 6;
            const milestones = ['Graft', 'Capped', 'Transfer', 'Emerge', 'Mating', 'Check'];
            milestones.forEach((label, idx) => {
              doc.text(label, marginLeft + 6 + (idx * colWidth) + colWidth/2, yPos + 5, { align: 'center' });
            });
            yPos += 8;
            
            // Timeline date row
            doc.setFillColor(255, 251, 235);
            doc.rect(marginLeft + 4, yPos, contentWidth - 4, 8, 'F');
            
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80);
            doc.setFontSize(7);
            
            // Short date format for timeline (e.g., "2 Jan")
            const shortDate = (date) => {
              if (!date) return '-';
              return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
            };
            
            const dates = [
              shortDate(timeline.graft),
              shortDate(timeline.capped),
              shortDate(timeline.transfer),
              shortDate(timeline.emerge),
              shortDate(timeline.mating),
              shortDate(timeline.check)
            ];
            dates.forEach((dateStr, idx) => {
              doc.text(dateStr, marginLeft + 6 + (idx * colWidth) + colWidth/2, yPos + 5, { align: 'center' });
            });
            yPos += 10;
          }
        });
        yPos += 10;
      }
      
      // Mating Nucs Table with Queen Transfer Info
      if (matingNucs.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 99, 235);
        doc.text('Mating Nucs', marginLeft, yPos);
        yPos += 8;
        
        // Separate occupied and available NUCs
        const occupiedNucs = matingNucs.filter(n => n.status === 'occupied');
        const availableNucs = matingNucs.filter(n => n.status !== 'occupied');
        
        // Queens in Progress section
        if (occupiedNucs.length > 0) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(234, 88, 12);
          doc.text(`Queens in Progress (${occupiedNucs.length})`, marginLeft + 2, yPos);
          yPos += 7;
          
          // Table header for occupied
          doc.setFillColor(234, 88, 12);
          doc.rect(marginLeft, yPos, contentWidth, headerHeight, 'F');
          doc.setTextColor(255);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const headerY = yPos + 6;
          doc.text('NUC #', marginLeft + 4, headerY);
          doc.text('Cell/Batch', marginLeft + 28, headerY);
          doc.text('Queen Status', marginLeft + 90, headerY);
          doc.text('Type', marginLeft + 135, headerY);
          doc.text('Location', marginLeft + 160, headerY);
          yPos += headerHeight + 1;
          
          // Rows for occupied NUCs
          doc.setFont('helvetica', 'normal');
          occupiedNucs.forEach((n, i) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            
            if (i % 2 === 0) {
              doc.setFillColor(255, 247, 237);
              doc.rect(marginLeft, yPos - 1, contentWidth, rowHeight, 'F');
            }
            
            doc.setTextColor(0);
            doc.setFontSize(8);
            const textY = yPos + 5;
            doc.text(String(n.nuc_number || '-'), marginLeft + 4, textY);
            
            // Cell/Batch info
            const cellBatch = n.current_batch_name 
              ? `#${n.current_cell_number} - ${n.current_batch_name.substring(0, 15)}`
              : '-';
            doc.text(cellBatch, marginLeft + 28, textY);
            
            // Queen status with color
            const queenStatus = n.queen_status ? toTitleCase(n.queen_status.replace('_', ' ')) : '-';
            doc.setTextColor(234, 88, 12);
            doc.text(queenStatus, marginLeft + 90, textY);
            doc.setTextColor(0);
            
            doc.text(toTitleCase(n.nuc_type || '-').substring(0, 12), marginLeft + 135, textY);
            doc.text(toTitleCase(n.location || '-').substring(0, 15), marginLeft + 160, textY);
            yPos += rowHeight;
          });
          yPos += 8;
        }
        
        // Available NUCs section
        if (availableNucs.length > 0) {
          if (yPos > 230) {
            doc.addPage();
            yPos = 20;
          }
          
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(22, 163, 74);
          doc.text(`Available NUCs (${availableNucs.length})`, marginLeft + 2, yPos);
          yPos += 7;
          
          // Table header for available
          doc.setFillColor(22, 163, 74);
          doc.rect(marginLeft, yPos, contentWidth, headerHeight, 'F');
          doc.setTextColor(255);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const headerY = yPos + 6;
          doc.text('NUC #', marginLeft + 4, headerY);
          doc.text('Type', marginLeft + 35, headerY);
          doc.text('Location', marginLeft + 75, headerY);
          doc.text('Status', marginLeft + 125, headerY);
          doc.text('Last Queen → Hive', marginLeft + 155, headerY);
          yPos += headerHeight + 1;
          
          // Rows for available NUCs
          doc.setFont('helvetica', 'normal');
          availableNucs.forEach((n, i) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            
            if (i % 2 === 0) {
              doc.setFillColor(240, 253, 244);
              doc.rect(marginLeft, yPos - 1, contentWidth, rowHeight, 'F');
            }
            
            doc.setTextColor(0);
            doc.setFontSize(8);
            const textY = yPos + 5;
            doc.text(String(n.nuc_number || '-'), marginLeft + 4, textY);
            doc.text(toTitleCase(n.nuc_type || '-').substring(0, 15), marginLeft + 35, textY);
            doc.text(toTitleCase(n.location || '-').substring(0, 18), marginLeft + 75, textY);
            doc.text(toTitleCase(n.status || '-'), marginLeft + 125, textY);
            
            // Show destination hive if a queen was graduated
            const destHive = n.destination_hive_number ? `Hive #${n.destination_hive_number}` : '-';
            doc.setTextColor(37, 99, 235);
            doc.text(destHive, marginLeft + 155, textY);
            doc.setTextColor(0);
            
            yPos += rowHeight;
          });
        }
      }
      
      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150);
        doc.text(`Queen Rearing Report - Page ${i} of ${pageCount}`, pageWidth / 2, 287, { align: 'center' });
      }
      
      doc.save(`queen-rearing-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Batch CRUD
  const handleSaveBatch = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const method = editingBatch ? 'PUT' : 'POST';
      const url = editingBatch 
        ? `${API}/api/queen-rearing/batches/${editingBatch.id}`
        : `${API}/api/queen-rearing/batches`;
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(batchForm) });
      if (res.ok) {
        toast.success(editingBatch ? 'Batch updated' : 'Batch created');
        setShowBatchDialog(false);
        setEditingBatch(null);
        resetBatchForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save batch');
    }
  };

  const handleDeleteBatch = async (id) => {
    if (!window.confirm('Delete this batch? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/queen-rearing/batches/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Batch deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  const handleArchiveBatch = async (id) => {
    try {
      const res = await fetch(`${API}/api/queen-rearing/batches/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' })
      });
      if (res.ok) {
        toast.success('Batch archived');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to archive batch');
    }
  };

  const handleUnarchiveBatch = async (id) => {
    try {
      const res = await fetch(`${API}/api/queen-rearing/batches/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' })
      });
      if (res.ok) {
        toast.success('Batch restored');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to restore batch');
    }
  };

  // Breeder CRUD
  const handleSaveBreeder = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const method = editingBreeder ? 'PUT' : 'POST';
      const url = editingBreeder 
        ? `${API}/api/queen-rearing/breeders/${editingBreeder.id}`
        : `${API}/api/queen-rearing/breeders`;
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(breederForm) });
      if (res.ok) {
        toast.success(editingBreeder ? 'Breeder updated' : 'Breeder created');
        setShowBreederDialog(false);
        setEditingBreeder(null);
        resetBreederForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save breeder');
    }
  };

  const handleDeleteBreeder = async (id) => {
    if (!window.confirm('Delete this breeder queen?')) return;
    try {
      const res = await fetch(`${API}/api/queen-rearing/breeders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Breeder deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete breeder');
    }
  };

  // Mating Nuc CRUD
  const handleSaveNuc = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const method = editingNuc ? 'PUT' : 'POST';
      const url = editingNuc 
        ? `${API}/api/queen-rearing/mating-nucs/${editingNuc.id}`
        : `${API}/api/queen-rearing/mating-nucs`;
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(nucForm) });
      if (res.ok) {
        toast.success(editingNuc ? 'Mating nuc updated' : 'Mating nuc created');
        setShowNucDialog(false);
        setEditingNuc(null);
        resetNucForm();
        await fetchData();
        
        // If we were adding a NUC from the transfer dialog, reopen it
        if (transferringCell) {
          setShowTransferDialog(true);
        }
      }
    } catch (error) {
      toast.error('Failed to save mating nuc');
    }
  };

  const handleDeleteNuc = async (id) => {
    if (!window.confirm('Delete this mating nuc?')) return;
    try {
      const res = await fetch(`${API}/api/queen-rearing/mating-nucs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Mating nuc deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete mating nuc');
    }
  };

  // Update cell status
  const handleUpdateCellStatus = async (batchId, cellId, newStatus, cellNumber = null) => {
    // If transferring, show dialog to select NUC (for both existing and new/placeholder cells)
    if (newStatus === 'transferred') {
      setTransferringCell({ batchId, cellId, cellNumber });
      setShowTransferDialog(true);
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      // Check if this is a new cell (placeholder)
      if (typeof cellId === 'string' && cellId.startsWith('new-')) {
        // Create new cell
        const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ cell_number: cellNumber, status: newStatus })
        });
        if (res.ok) {
          toast.success('Cell created and status updated');
          fetchData();
        }
      } else {
        // Update existing cell
        const body = { status: newStatus };
        const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells/${cellId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body)
        });
        if (res.ok) {
          toast.success('Cell status updated');
          fetchData();
        }
      }
    } catch (error) {
      toast.error('Failed to update cell');
    }
  };

  // Put a capped cell straight into a hive (queenless colony or split)
  const handleTransferToHive = async (hive) => {
    if (!transferringCell) return;
    setTransferBusy(true);
    try {
      const { batchId, cellId, cellNumber } = transferringCell;
      const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells/${cellId}/to-hive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hive_id: hive.id, cell_number: cellNumber })
      });
      if (!res.ok) throw new Error();
      toast.success(`Cell #${cellNumber} placed in Hive ${hive.hive_number}`, { description: 'Queen check reminders added to Tasks and Calendar.' });
      fetchData();
      window.dispatchEvent(new Event('refreshData'));
      setShowTransferDialog(false);
      setTransferringCell(null);
    } catch {
      toast.error('Failed to put the cell into the hive');
    } finally {
      setTransferBusy(false);
    }
  };

  // Handle transfer to NUC
  const handleTransferToNuc = async (nucId) => {
    if (!transferringCell) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const { batchId, cellId, cellNumber } = transferringCell;
      
      // Find the batch to get batch name
      const batch = batches.find(b => b.id === batchId);
      const batchName = batch?.batch_name || `Batch #${batch?.batch_number || '?'}`;
      
      // Check if this is a new cell
      let newCellId = cellId;
      if (typeof cellId === 'string' && cellId.startsWith('new-')) {
        // Create new cell with transferred status
        const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ cell_number: cellNumber, status: 'transferred', nuc_id: nucId })
        });
        if (res.ok) {
          const data = await res.json();
          newCellId = data.cell?.id || cellId;
        } else {
          toast.error('Failed to create cell');
          return;
        }
      } else {
        // Update existing cell
        const res = await fetch(`${API}/api/queen-rearing/batches/${batchId}/cells/${cellId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: 'transferred', nuc_id: nucId })
        });
        if (!res.ok) {
          toast.error('Failed to update cell');
          return;
        }
      }
      
      // Update the NUC to link the cell and set status to occupied
      const nucRes = await fetch(`${API}/api/queen-rearing/mating-nucs/${nucId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          status: 'occupied',
          current_cell_id: newCellId.startsWith('new-') ? '' : newCellId,
          current_batch_id: batchId,
          current_cell_number: cellNumber,
          current_batch_name: batchName,
          queen_status: 'cell_transferred'
        })
      });
      
      if (nucRes.ok) {
        toast.success('Cell transferred to NUC');
        fetchData();
      } else {
        toast.error('Cell transferred but failed to update NUC');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to transfer cell');
    }
    
    setShowTransferDialog(false);
    setTransferringCell(null);
  };

  // Assign queen to hive
  const handleAssignQueen = async (hiveId) => {
    if (!selectedCell) return;
    try {
      const res = await fetch(`${API}/api/queen-rearing/assign-queen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch_id: selectedCell.batchId,
          cell_id: selectedCell.cellId,
          hive_id: hiveId
        })
      });
      if (res.ok) {
        toast.success('Queen assigned to hive');
        setShowAssignDialog(false);
        setSelectedCell(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign queen');
    }
  };

  // Update queen status on a NUC (emerged, mated, laying, failed)
  const handleUpdateQueenStatus = async (nucId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/api/queen-rearing/mating-nucs/${nucId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ queen_status: newStatus })
      });
      if (res.ok) {
        toast.success(`Queen status updated to ${newStatus}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update queen status');
    }
  };

  // Move laying queen from NUC to a hive
  const handleMoveQueenToHive = async (hiveId) => {
    if (!movingNuc) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const hive = hives.find(h => h.id === hiveId);
      
      // Call the backend endpoint that handles both NUC and Hive updates
      const res = await fetch(`${API}/api/queen-rearing/move-queen-to-hive`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          nuc_id: movingNuc.id,
          hive_id: hiveId
        })
      });
      
      if (res.ok) {
        toast.success(`Queen moved to Hive #${hive?.hive_number}`);
        fetchData();
      } else {
        toast.error('Failed to move queen to hive');
      }
    } catch (error) {
      toast.error('Failed to move queen to hive');
    }
    
    setShowMoveToHiveDialog(false);
    setMovingNuc(null);
  };

  // Mark NUC queen as failed and make NUC available again
  const handleQueenFailed = async (nucId) => {
    if (!window.confirm('Mark this queen as failed? The NUC will become available again.')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/api/queen-rearing/mating-nucs/${nucId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          status: 'available',
          queen_status: '',
          current_cell_id: '',
          current_batch_id: '',
          current_cell_number: 0,
          current_batch_name: ''
        })
      });
      if (res.ok) {
        toast.success('NUC marked as available');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update NUC');
    }
  };

  // Reset forms
  const resetBatchForm = () => setBatchForm({
    batch_name: '', graft_date: '', breeder_queen_id: '', breeder_queen_name: '',
    breeder_hive_id: '', breeder_hive_number: '', yard_id: '', yard_name: '', cell_builder_hive_id: '',
    cell_builder_hive_number: '', cell_builder_method: 'queenless',
    finisher_hive_id: '', finisher_hive_number: '',
    cells_grafted: 10, cells_per_frame: 15,
    grafting_tool: 'chinese', cell_cups: 'plastic', incubator_used: false, notes: ''
  });
  
  const resetBreederForm = () => setBreederForm({
    name: '', source_hive_id: '', source_hive_number: '', yard_id: '', yard_name: '', genetics: '',
    traits: [], acquired_date: '', source: '', notes: '', status: 'active'
  });
  
  const resetNucForm = () => setNucForm({
    nuc_number: '', nuc_type: 'apidea', location: '', yard_id: '', yard_name: '', status: 'available', notes: ''
  });

  // Helper functions
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const str = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString();
    const parts = str.split('T')[0].split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    }
    return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const getStatusColor = (status) => {
    const colors = {
      grafted: 'bg-blue-100 text-blue-800',
      accepted: 'bg-cyan-100 text-cyan-800',
      capped: 'bg-purple-100 text-purple-800',
      transferred: 'bg-indigo-100 text-indigo-800',
      emerged: 'bg-pink-100 text-pink-800',
      mated: 'bg-orange-100 text-orange-800',
      laying: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      planning: 'bg-gray-100 text-gray-800',
      building: 'bg-amber-100 text-amber-800',
      complete: 'bg-green-100 text-green-800',
      archived: 'bg-stone-100 text-stone-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    // Coerce to string in case a Date object or number is passed
    const str = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString();
    // Parse YYYY-MM-DD as local date (not UTC)
    const parts = str.split('T')[0].split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-amber-50 to-orange-50 flex items-center justify-center py-20">
        <div className="text-center">
          <Crown className="h-12 w-12 text-amber-600 animate-pulse mx-auto mb-4" />
          <p className="text-stone-600">Loading Queen Rearing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen honeycomb-bg">
      {/* Header */}
      <div className="p-4 mb-4">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <Crown className="h-6 w-6 text-amber-600" />
                Queen Rearing
              </h1>
              <p className="text-sm text-stone-500 mt-1">Manage your queen breeding program</p>
            </div>
            <PageTourButton pageView="queen-rearing" />
          </div>
        </div>
      </div>

      <div className="px-4">
      <div className="max-w-7xl space-y-3 mb-4">
        <QueenReminderAlerts token={token} onOpenBatch={openBatchCells} />
        {saleReadyBatches.map(b => (
          <div key={b.id} data-testid={`sale-ready-banner-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal-200 bg-gradient-to-r from-teal-50 to-white px-4 py-3">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-teal-600" />
              <div>
                <p className="font-semibold text-teal-900">{b.laying > 0 ? `${b.laying} laying queen${b.laying !== 1 ? 's' : ''} ready` : 'Queens ready for their laying check'}: {b.batch_name || `Batch #${b.batch_number}`}</p>
                <p className="text-xs text-teal-700">{b.yard_name ? `📍 ${b.yard_name} · ` : ''}Day {b.day} since grafting</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`sale-ready-open-${b.id}`} size="sm" variant="outline" className="border-teal-300 text-teal-700" onClick={() => setActiveTab('nucs')}>Check Nucs</Button>
              <Button data-testid={`sale-ready-list-${b.id}`} size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => listQueensForSale(b.id)}>
                <ShoppingBag className="h-4 w-4 mr-1" /> List queens for sale
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Stats Summary */}
      <div className="max-w-7xl mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-amber-100 border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-amber-800">{filteredBatches.filter(b => b.status !== 'archived').length}</div>
            <div className="text-sm text-amber-600">Active Batches</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-100 border-purple-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-800">{filteredBreeders.filter(b => b.status === 'active').length}</div>
            <div className="text-sm text-purple-600">Breeder Queens</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-800">{filteredNucs.length}</div>
            <div className="text-sm text-blue-600">Mating Nucs</div>
          </CardContent>
        </Card>
        <Card className="bg-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-800">
              {filteredBatches.reduce((sum, b) => sum + (b.queens_laying || 0), 0)}
            </div>
            <div className="text-sm text-green-600">Queens Laying</div>
          </CardContent>
        </Card>
        <Card className="bg-pink-100 border-pink-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-pink-800">
              {filteredBatches.reduce((sum, b) => sum + (b.cells_grafted || 0), 0)}
            </div>
            <div className="text-sm text-pink-600">Total Cells</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mb-6">
        <div className="flex gap-2 flex-wrap items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'breeders', label: '👑 Breeders', count: filteredBreeders.length },
              { id: 'batches', label: '📦 Batches', count: filteredBatches.length },
              { id: 'calendar', label: '📅 Timeline', count: filteredBatches.filter(b => b.status !== 'archived' && b.graft_date).length },
              { id: 'nucs', label: '🏠 Mating Nucs', count: filteredNucs.length }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                {tab.label}
                <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">{tab.count}</span>
              </Button>
            ))}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {yards.length > 0 && (
              <div className="flex items-center gap-1.5" data-testid="queen-yard-filter">
                <MapPin className="h-4 w-4 text-stone-400" />
                <Select value={filterYard} onValueChange={setFilterYard}>
                  <SelectTrigger data-testid="queen-yard-filter-select" className="w-[170px] h-9 text-sm">
                    <SelectValue placeholder="All Apiaries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Apiaries</SelectItem>
                    {yards.map(y => (
                      <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filterYard !== 'all' && (
                  <button data-testid="queen-yard-filter-clear" onClick={() => setFilterYard('all')} className="text-xs text-stone-400 hover:text-stone-600">Clear</button>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={exportToCSV} className="text-amber-700 border-amber-300 hover:bg-amber-50">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF} className="text-amber-700 border-amber-300 hover:bg-amber-50">
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
        
      </div>

      {/* Content */}
      <div className="max-w-7xl">
        {/* Batches Tab */}
        {activeTab === 'batches' && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => { resetBatchForm(); setEditingBatch(null); setShowBatchDialog(true); }} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" /> New Batch
              </Button>
              {batches.some(b => b.cells?.some(c => c.status === 'capped')) && (
                <Button onClick={() => setShowBulkTransferDialog(true)} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="quick-transfer-btn">
                  <Home className="h-4 w-4 mr-2" /> Quick Transfer Capped Cells
                </Button>
              )}
            </div>
            
            {filteredBatches.length === 0 ? (
              <Card className="text-center p-8">
                <Crown className="h-16 w-16 text-amber-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700">{filterYard !== 'all' ? `No Batches in ${filterYard}` : 'No Batches Yet'}</h3>
                <p className="text-stone-500">{filterYard !== 'all' ? 'Try a different apiary' : 'Start your first queen rearing batch'}</p>
              </Card>
            ) : (
              <>
                {/* Active batches */}
                {filteredBatches.filter(b => b.status !== 'archived').map(batch => (
                <BatchCard
                  key={batch.id}
                  batch={batch}
                  focused={focusBatchId === batch.id}
                  onFocusDone={() => setFocusBatchId(null)}
                  onEdit={() => { 
                    setBatchForm({
                      batch_name: batch.batch_name || '',
                      graft_date: batch.graft_date || '',
                      breeder_queen_id: batch.breeder_queen_id || '',
                      breeder_queen_name: batch.breeder_queen_name || '',
                      breeder_hive_id: batch.breeder_hive_id || '',
                      breeder_hive_number: batch.breeder_hive_number || '',
                      yard_id: batch.yard_id || '',
                      yard_name: batch.yard_name || '',
                      cell_builder_hive_id: batch.cell_builder_hive_id || '',
                      cell_builder_hive_number: batch.cell_builder_hive_number || '',
                      cell_builder_method: batch.cell_builder_method || 'queenless',
                      finisher_hive_id: batch.finisher_hive_id || '',
                      finisher_hive_number: batch.finisher_hive_number || '',
                      cells_grafted: batch.cells_grafted || 10,
                      cells_per_frame: batch.cells_per_frame || 15,
                      grafting_tool: batch.grafting_tool || 'chinese',
                      cell_cups: batch.cell_cups || 'plastic',
                      incubator_used: batch.incubator_used || false,
                      notes: batch.notes || ''
                    });
                    setEditingBatch(batch); 
                    setShowBatchDialog(true); 
                  }}
                  onDelete={() => handleDeleteBatch(batch.id)}
                  onArchive={() => handleArchiveBatch(batch.id)}
                  onUnarchive={() => handleUnarchiveBatch(batch.id)}
                  onUpdateCell={handleUpdateCellStatus}
                  onAssignQueen={(cellId) => { setSelectedCell({ batchId: batch.id, cellId }); setShowAssignDialog(true); }}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                  getDaysUntil={getDaysUntil}
                />
              ))}

                {/* Archived Batches Section */}
                {filteredBatches.some(b => b.status === 'archived') && (
                  <Card className="border-stone-200 bg-stone-50" data-testid="archived-batches-section">
                    <button
                      onClick={() => setShowArchivedBatches(!showArchivedBatches)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-100 transition-colors"
                      data-testid="toggle-archived-batches"
                    >
                      <div className="flex items-center gap-2">
                        <Archive className="h-4 w-4 text-stone-400" />
                        <span className="text-sm font-medium text-stone-600">
                          Archived Batches ({filteredBatches.filter(b => b.status === 'archived').length})
                        </span>
                      </div>
                      <div className={`p-1 rounded ${showArchivedBatches ? 'bg-stone-200' : 'bg-stone-100'}`}>
                        {showArchivedBatches ? <ChevronUp className="h-4 w-4 text-stone-500" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
                      </div>
                    </button>
                    {showArchivedBatches && (
                      <div className="px-4 pb-4 space-y-3">
                        {filteredBatches.filter(b => b.status === 'archived').map(batch => (
                          <BatchCard
                            key={batch.id}
                            batch={batch}
                            onEdit={() => {
                              setBatchForm({
                                batch_name: batch.batch_name || '',
                                graft_date: batch.graft_date || '',
                                breeder_queen_id: batch.breeder_queen_id || '',
                                breeder_queen_name: batch.breeder_queen_name || '',
                                breeder_hive_id: batch.breeder_hive_id || '',
                                breeder_hive_number: batch.breeder_hive_number || '',
                      yard_id: batch.yard_id || '',
                      yard_name: batch.yard_name || '',
                                cell_builder_hive_id: batch.cell_builder_hive_id || '',
                                cell_builder_hive_number: batch.cell_builder_hive_number || '',
                                cell_builder_method: batch.cell_builder_method || 'queenless',
                                finisher_hive_id: batch.finisher_hive_id || '',
                                finisher_hive_number: batch.finisher_hive_number || '',
                                cells_grafted: batch.cells_grafted || 10,
                                cells_per_frame: batch.cells_per_frame || 15,
                                grafting_tool: batch.grafting_tool || 'chinese',
                                cell_cups: batch.cell_cups || 'plastic',
                                incubator_used: batch.incubator_used || false,
                                notes: batch.notes || ''
                              });
                              setEditingBatch(batch);
                              setShowBatchDialog(true);
                            }}
                            onDelete={() => handleDeleteBatch(batch.id)}
                            onArchive={() => handleArchiveBatch(batch.id)}
                            onUnarchive={() => handleUnarchiveBatch(batch.id)}
                            onUpdateCell={handleUpdateCellStatus}
                            onAssignQueen={(cellId) => { setSelectedCell({ batchId: batch.id, cellId }); setShowAssignDialog(true); }}
                            formatDate={formatDate}
                            getStatusColor={getStatusColor}
                            getDaysUntil={getDaysUntil}
                          />
                        ))}
                      </div>
                    )}
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* Calendar Tab - Timeline per Batch */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            <div data-testid="graft-reminders-note" className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <Bell className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">Reminders are sent the day before and on the day of capping, transfer and emergence, plus a Queens Ready alert on Day 30. Each one includes the apiary name.</span>
              <button data-testid="print-all-calendars-btn" onClick={() => printGraftCalendar(filteredBatches.filter(b => b.status !== 'archived'))} className="shrink-0 flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700">
                <CalendarDays className="h-3.5 w-3.5" /> Print all calendars
              </button>
              <button data-testid="queen-reminder-settings-btn" onClick={() => setShowReminderSettings(true)} className="shrink-0 flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-amber-300 hover:bg-amber-100">
                <Settings className="h-3.5 w-3.5" /> Reminder settings
              </button>
            </div>
            <Dialog open={showReminderSettings} onOpenChange={setShowReminderSettings}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-amber-600" /> Notification Settings</DialogTitle></DialogHeader>
                <QueenReminderSettings token={token} />
              </DialogContent>
            </Dialog>
            {filteredBatches.filter(b => b.status !== 'archived' && b.graft_date).length === 0 ? (
              <Card className="text-center p-8" data-testid="timeline-empty-state">
                <Calendar className="h-16 w-16 text-amber-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700">{filterYard !== 'all' ? `No Batches in ${filterYard}` : 'No Batches Yet'}</h3>
                <p className="text-stone-500">Create a batch with a graft date to see the timeline</p>
              </Card>
            ) : (
              filteredBatches.filter(b => b.status !== 'archived').map(batch => (
                <BatchTimelineCard
                  key={batch.id}
                  batch={batch}
                  formatDate={formatDate}
                  getDaysUntil={getDaysUntil}
                  actions={{
                    onBulkStatus: handleBulkCellStatus,
                    onTransfer: openTransferForBatch,
                    onGoToNucs: () => setActiveTab('nucs'),
                    onOpenCells: openBatchCells,
                    onListQueens: listQueensForSale
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* Breeders Tab */}
        {activeTab === 'breeders' && (
          <div className="space-y-4">
            <Button onClick={() => { resetBreederForm(); setEditingBreeder(null); setShowBreederDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" /> Add Breeder Queen
            </Button>
            
            {filteredBreeders.length === 0 ? (
              <Card className="text-center p-8">
                <Heart className="h-16 w-16 text-purple-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700">No Breeder Queens</h3>
                <p className="text-stone-500">Add your first breeder queen to track genetics</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBreeders.map(breeder => (
                  <Card key={breeder.id} className={breeder.status === 'active' ? 'border-purple-200' : 'opacity-60'}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                            👑 {breeder.name}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(breeder.status)}`}>
                            {breeder.status}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setBreederForm(breeder); setEditingBreeder(breeder); setShowBreederDialog(true); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteBreeder(breeder.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {breeder.genetics && <p className="text-sm text-stone-600">Genetics: {breeder.genetics}</p>}
                      {breeder.yard_name && <p data-testid={`breeder-apiary-${breeder.id}`} className="text-sm text-teal-700 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {breeder.yard_name}</p>}
                      {breeder.source_hive_number && <p className="text-sm text-stone-500">From: Hive {breeder.source_hive_number}</p>}
                      {breeder.traits?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {breeder.traits.map(trait => (
                            <span key={trait} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{trait}</span>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mating Nucs Tab - Enhanced with Queen Lifecycle Tracking */}
        {activeTab === 'nucs' && (
          <div className="space-y-6">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => { resetNucForm(); setEditingNuc(null); setShowNucDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" /> Add Mating Nuc
              </Button>
            </div>
            
            {filteredNucs.length === 0 ? (
              <Card className="text-center p-8">
                <Home className="h-16 w-16 text-blue-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700">No Mating Nucs</h3>
                <p className="text-stone-500">Add mating nucs to track queen development</p>
              </Card>
            ) : (
              <>
                {/* Occupied NUCs - Queens in Progress */}
                {filteredNucs.filter(n => n.status === 'occupied').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <Crown className="h-5 w-5" /> Queens in Progress ({filteredNucs.filter(n => n.status === 'occupied').length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredNucs.filter(n => n.status === 'occupied').map(nuc => (
                        <Card key={nuc.id} className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="font-bold text-lg text-stone-800 flex items-center gap-2">
                                  <Home className="h-5 w-5 text-amber-600" />
                                  NUC #{nuc.nuc_number}
                                </h3>
                                <span className="text-xs text-stone-500">{nuc.nuc_type} {nuc.location && `• ${nuc.location}`} {nuc.yard_name && nuc.yard_name !== nuc.location && `• 📍 ${nuc.yard_name}`}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => { setNucForm(nuc); setEditingNuc(nuc); setShowNucDialog(true); }}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Cell Info */}
                            {nuc.current_batch_name && (
                              <div className="bg-white/60 rounded-lg p-2 mb-3 text-sm">
                                <div className="text-stone-600">
                                  <span className="font-medium">Cell #{nuc.current_cell_number}</span> from <span className="font-medium">{nuc.current_batch_name}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Queen Status Progress */}
                            <div className="mb-3">
                              <div className="text-xs font-medium text-stone-600 mb-2">Queen Lifecycle:</div>
                              <div className="flex gap-1">
                                {['cell_transferred', 'emerged', 'mated', 'laying'].map((status, idx) => {
                                  const statusLabels = { cell_transferred: 'Cell', emerged: 'Emerged', mated: 'Mated', laying: 'Laying' };
                                  const statusColors = {
                                    cell_transferred: 'bg-indigo-500',
                                    emerged: 'bg-pink-500',
                                    mated: 'bg-orange-500',
                                    laying: 'bg-green-500'
                                  };
                                  const statusOrder = ['cell_transferred', 'emerged', 'mated', 'laying'];
                                  const currentIdx = statusOrder.indexOf(nuc.queen_status);
                                  const isActive = idx <= currentIdx;
                                  const isCurrent = status === nuc.queen_status;
                                  
                                  return (
                                    <div
                                      key={status}
                                      className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-medium transition-all ${
                                        isActive ? `${statusColors[status]} text-white` : 'bg-gray-200 text-gray-500'
                                      } ${isCurrent ? 'ring-2 ring-offset-1 ring-amber-400' : ''}`}
                                    >
                                      {statusLabels[status]}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            
                            {/* Action Buttons based on current status */}
                            <div className="flex gap-2 flex-wrap">
                              {nuc.queen_status === 'cell_transferred' && (
                                <Button size="sm" className="bg-pink-600 hover:bg-pink-700" onClick={() => handleUpdateQueenStatus(nuc.id, 'emerged')}>
                                  <Egg className="h-3 w-3 mr-1" /> Mark Emerged
                                </Button>
                              )}
                              {nuc.queen_status === 'emerged' && (
                                <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => handleUpdateQueenStatus(nuc.id, 'mated')}>
                                  <Heart className="h-3 w-3 mr-1" /> Mark Mated
                                </Button>
                              )}
                              {nuc.queen_status === 'mated' && (
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateQueenStatus(nuc.id, 'laying')}>
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Confirm Laying
                                </Button>
                              )}
                              {nuc.queen_status === 'laying' && (
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => { setMovingNuc(nuc); setShowMoveToHiveDialog(true); }}>
                                  <Home className="h-3 w-3 mr-1" /> Move to Hive
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleQueenFailed(nuc.id)}>
                                <X className="h-3 w-3 mr-1" /> Failed
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Available NUCs */}
                {filteredNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" /> Available NUCs ({filteredNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {filteredNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').map(nuc => (
                        <Card key={nuc.id} className={`border-2 ${
                          nuc.status === 'available' ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-stone-800">NUC #{nuc.nuc_number}</h3>
                                <span className="text-xs text-stone-500">{nuc.nuc_type}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => { setNucForm(nuc); setEditingNuc(nuc); setShowNucDialog(true); }}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteNuc(nuc.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              nuc.status === 'available' ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'
                            }`}>
                              {nuc.status === 'needs-bees' ? 'Needs Bees' : 'Available'}
                            </span>
                            {nuc.location && <p className="text-xs text-stone-500 mt-2">{nuc.location}</p>}
                            {nuc.yard_name && <p data-testid={`nuc-apiary-${nuc.id}`} className="text-xs text-teal-700 mt-1">📍 {nuc.yard_name}</p>}
                            {nuc.destination_hive_number && (
                              <p className="text-xs text-blue-600 mt-1">Last queen → Hive #{nuc.destination_hive_number}</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Batch Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-600" />
              {editingBatch ? 'Edit Batch' : 'New Queen Rearing Batch'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Batch Name</Label>
                <Input
                  value={batchForm.batch_name}
                  onChange={(e) => setBatchForm({ ...batchForm, batch_name: e.target.value })}
                  placeholder="e.g., Spring Batch 2025"
                />
              </div>
              <div>
                <Label>Grafting Date</Label>
                <Input
                  type="date"
                  value={batchForm.graft_date}
                  onChange={(e) => setBatchForm({ ...batchForm, graft_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Apiary</Label>
              <Select
                value={batchForm.yard_name || 'none'}
                onValueChange={(v) => setBatchForm({ ...batchForm, yard_name: v === 'none' ? '' : v, yard_id: v === 'none' ? '' : (yards.find(y => y.name === v)?.id || '') })}
              >
                <SelectTrigger data-testid="batch-form-apiary-select"><SelectValue placeholder="Select apiary" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No apiary</SelectItem>
                  {yards.map(y => <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500 mt-1">Where this batch is being reared — hive lists below are limited to this apiary</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Breeder Queen</Label>
                <Select
                  value={batchForm.breeder_queen_id}
                  onValueChange={(value) => {
                    const breeder = breeders.find(b => b.id === value);
                    setBatchForm({
                      ...batchForm,
                      breeder_queen_id: value,
                      breeder_queen_name: breeder?.name || ''
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select breeder" /></SelectTrigger>
                  <SelectContent>
                    {enrichedBreeders.filter(b => b.status === 'active' && (!batchForm.yard_name || !b.yard_name || b.yard_name === batchForm.yard_name)).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}{b.yard_name ? ` · ${b.yard_name}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Starter Hive (Queenless)</Label>
                <Select
                  value={batchForm.cell_builder_hive_id}
                  onValueChange={(value) => {
                    const hive = hives.find(h => h.id === value);
                    setBatchForm({
                      ...batchForm,
                      cell_builder_hive_id: value,
                      cell_builder_hive_number: hive?.hive_number?.toString() || '',
                      ...(!batchForm.yard_name && hive?.yard ? { yard_name: hive.yard, yard_id: yards.find(y => y.name === hive.yard)?.id || '' } : {})
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select hive" /></SelectTrigger>
                  <SelectContent>
                    {formHives(batchForm.yard_name).map(h => (
                      <SelectItem key={h.id} value={h.id}>{hiveLabel(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500 mt-1">Queenless hive for initial cell building</p>
              </div>
              <div>
                <Label>Finisher Hive (Queenright + Excluder)</Label>
                <Select
                  value={batchForm.finisher_hive_id || 'same'}
                  onValueChange={(value) => {
                    if (value === 'same') {
                      setBatchForm({
                        ...batchForm,
                        finisher_hive_id: '',
                        finisher_hive_number: ''
                      });
                    } else {
                      const hive = hives.find(h => h.id === value);
                      setBatchForm({
                        ...batchForm,
                        finisher_hive_id: value,
                        finisher_hive_number: hive?.hive_number?.toString() || ''
                      });
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select hive" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same">Same as Starter</SelectItem>
                    {formHives(batchForm.yard_name).filter(h => h.id !== batchForm.cell_builder_hive_id).map(h => (
                      <SelectItem key={h.id} value={h.id}>{hiveLabel(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500 mt-1">Move cells here after 24 hrs (above excluder)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label>Cells Grafted</Label>
                <Input
                  type="number"
                  value={batchForm.cells_grafted}
                  onChange={(e) => setBatchForm({ ...batchForm, cells_grafted: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Cells per Frame</Label>
                <Input
                  type="number"
                  value={batchForm.cells_per_frame}
                  onChange={(e) => setBatchForm({ ...batchForm, cells_per_frame: parseInt(e.target.value) || 15 })}
                  placeholder="15"
                />
                <p className="text-xs text-stone-500 mt-1">
                  = {Math.ceil((batchForm.cells_grafted || 0) / (batchForm.cells_per_frame || 15))} frames
                </p>
              </div>
              <div>
                <Label>Grafting Tool</Label>
                <Select value={batchForm.grafting_tool} onValueChange={(v) => setBatchForm({ ...batchForm, grafting_tool: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chinese">Chinese Tool</SelectItem>
                    <SelectItem value="german">German Tool</SelectItem>
                    <SelectItem value="jenter">Jenter System</SelectItem>
                    <SelectItem value="nicot">Nicot System</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cell Cups</Label>
                <Select value={batchForm.cell_cups} onValueChange={(v) => setBatchForm({ ...batchForm, cell_cups: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plastic">Plastic Cups</SelectItem>
                    <SelectItem value="wax">Wax Cups</SelectItem>
                    <SelectItem value="jzbz">JzBz Cups</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={batchForm.notes}
                onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })}
                placeholder="Any additional notes..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBatchDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveBatch} className="bg-amber-600 hover:bg-amber-700">
                {editingBatch ? 'Update' : 'Create'} Batch
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Breeder Dialog */}
      <Dialog open={showBreederDialog} onOpenChange={setShowBreederDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>👑 {editingBreeder ? 'Edit' : 'Add'} Breeder Queen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={breederForm.name}
                onChange={(e) => setBreederForm({ ...breederForm, name: e.target.value })}
                placeholder="e.g., Italian Queen #1"
              />
            </div>
            <div>
              <Label>Apiary</Label>
              <Select
                value={breederForm.yard_name || 'none'}
                onValueChange={(v) => setBreederForm({ ...breederForm, yard_name: v === 'none' ? '' : v, yard_id: v === 'none' ? '' : (yards.find(y => y.name === v)?.id || '') })}
              >
                <SelectTrigger data-testid="breeder-form-apiary-select"><SelectValue placeholder="Select apiary" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No apiary</SelectItem>
                  {yards.map(y => <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Genetics</Label>
                <Select value={breederForm.genetics} onValueChange={(v) => setBreederForm({ ...breederForm, genetics: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="italian">Italian</SelectItem>
                    <SelectItem value="carniolan">Carniolan</SelectItem>
                    <SelectItem value="buckfast">Buckfast</SelectItem>
                    <SelectItem value="russian">Russian</SelectItem>
                    <SelectItem value="vsh">VSH</SelectItem>
                    <SelectItem value="saskatraz">Saskatraz</SelectItem>
                    <SelectItem value="local">Local/Mixed</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source Hive</Label>
                <Select
                  value={breederForm.source_hive_id}
                  onValueChange={(value) => {
                    const hive = hives.find(h => h.id === value);
                    setBreederForm({
                      ...breederForm,
                      source_hive_id: value,
                      source_hive_number: hive?.hive_number?.toString() || '',
                      ...(hive?.yard ? { yard_name: hive.yard, yard_id: yards.find(y => y.name === hive.yard)?.id || '' } : {})
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {formHives(breederForm.yard_name).map(h => (
                      <SelectItem key={h.id} value={h.id}>{hiveLabel(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={breederForm.notes}
                onChange={(e) => setBreederForm({ ...breederForm, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBreederDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveBreeder} className="bg-purple-600 hover:bg-purple-700">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mating Nuc Dialog */}
      <Dialog open={showNucDialog} onOpenChange={setShowNucDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>🏠 {editingNuc ? 'Edit' : 'Add'} Mating Nuc</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nuc Number</Label>
                <Input
                  value={nucForm.nuc_number}
                  onChange={(e) => setNucForm({ ...nucForm, nuc_number: e.target.value })}
                  placeholder="e.g., 1, 2, A, B"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={nucForm.nuc_type} onValueChange={(v) => setNucForm({ ...nucForm, nuc_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apidea">Apidea</SelectItem>
                    <SelectItem value="mini-plus">Mini-Plus</SelectItem>
                    <SelectItem value="kieler">Kieler</SelectItem>
                    <SelectItem value="5-frame">5-Frame Nuc</SelectItem>
                    <SelectItem value="3-frame">3-Frame Nuc</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Apiary</Label>
              <Select
                value={nucForm.yard_name || 'none'}
                onValueChange={(v) => setNucForm({ ...nucForm, yard_name: v === 'none' ? '' : v, yard_id: v === 'none' ? '' : (yards.find(y => y.name === v)?.id || '') })}
              >
                <SelectTrigger data-testid="nuc-form-apiary-select"><SelectValue placeholder="Select apiary" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No apiary</SelectItem>
                  {yards.map(y => <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={nucForm.location}
                onChange={(e) => setNucForm({ ...nucForm, location: e.target.value })}
                placeholder="e.g., Back apiary, Mating apiary"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={nucForm.status} onValueChange={(v) => setNucForm({ ...nucForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="needs-bees">Needs Bees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNucDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveNuc} className="bg-blue-600 hover:bg-blue-700">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Queen Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>🏠 Assign Queen to Hive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-stone-600">Select the hive where this queen will be installed:</p>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {hives.map(hive => (
                <Button
                  key={hive.id}
                  variant="outline"
                  className="justify-start h-auto py-3"
                  onClick={() => handleAssignQueen(hive.id)}
                >
                  <div className="text-left">
                    <div className="font-semibold">Hive {hive.hive_number}</div>
                    <div className="text-xs text-stone-500">{hive.yard}</div>
                  </div>
                </Button>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer to NUC Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{transferTarget === 'hive' ? '👑 Put Cell Into a Hive' : '🏠 Transfer Cell to Mating NUC'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <TransferTargetToggle value={transferTarget} onChange={setTransferTarget} testPrefix="transfer" />
            {transferTarget === 'hive' ? (
              <CellHivePicker open={showTransferDialog && transferTarget === 'hive'} onPick={handleTransferToHive} busy={transferBusy} />
            ) : (<>
            <p className="text-sm text-stone-600">Select a mating NUC for this queen cell:</p>
            
            {matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length === 0 ? (
              <div className="text-center py-4">
                <Home className="h-12 w-12 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-500 mb-4">No available mating NUCs</p>
                <Button 
                  onClick={() => { 
                    setShowTransferDialog(false); 
                    resetNucForm(); 
                    setEditingNuc(null); 
                    setShowNucDialog(true); 
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Mating NUC
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').map(nuc => (
                  <Button
                    key={nuc.id}
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3"
                    onClick={() => handleTransferToNuc(nuc.id)}
                  >
                    <Home className="h-4 w-4 mr-2 text-indigo-600" />
                    <div>
                      <div className="font-medium">NUC #{nuc.nuc_number}</div>
                      <div className="text-xs text-stone-500">
                        {nuc.nuc_type} {nuc.location && `• ${nuc.location}`}
                        {nuc.status === 'needs-bees' && <span className="text-orange-600 ml-1">• needs bees</span>}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            )}
            </>)}
            
            <div className="flex gap-2 pt-2 border-t">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => { 
                  resetNucForm(); 
                  setEditingNuc(null); 
                  setShowNucDialog(true); 
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add New NUC
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => { setShowTransferDialog(false); setTransferringCell(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Queen to Hive Dialog */}
      <Dialog open={showMoveToHiveDialog} onOpenChange={setShowMoveToHiveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-green-600" />
              Move Laying Queen to Hive
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {movingNuc && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <span className="font-semibold">NUC #{movingNuc.nuc_number}</span> has a laying queen ready to be moved.
                </p>
                {movingNuc.current_batch_name && (
                  <p className="text-xs text-green-600 mt-1">
                    Cell #{movingNuc.current_cell_number} from {movingNuc.current_batch_name}
                  </p>
                )}
              </div>
            )}
            
            <p className="text-sm text-stone-600">Select the hive where this queen will be installed:</p>
            
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {/* Priority: Queenless hives first, then Ready hives, then recent splits, then others */}
              {(() => {
                const allHivesList = hives.filter(h => h.type !== 'nuc');
                
                // Find queenless hives (from latest inspection)
                const queenlessIds = new Set();
                const splitNoQueenIds = new Set();
                
                inspections.forEach(insp => {
                  if (insp.queen_seen === false || insp.queen_seen === 'No' || 
                      insp.queenless === true || insp.queenless === 'Yes' ||
                      insp.queen_status === 'queenless') {
                    queenlessIds.add(insp.hive_id);
                  }
                  // If a later inspection shows queen present, remove from queenless
                  if (insp.queen_seen === true || insp.queen_seen === 'Yes') {
                    queenlessIds.delete(insp.hive_id);
                  }
                });
                
                // Find hives from splits (may need queen)
                allHivesList.forEach(h => {
                  if (h.is_split && h.split_type === 'walk-away') {
                    splitNoQueenIds.add(h.id);
                  }
                });
                
                const queenless = allHivesList.filter(h => queenlessIds.has(h.id));
                const ready = allHivesList.filter(h => h.status === 'ready' && !queenlessIds.has(h.id));
                const splits = allHivesList.filter(h => splitNoQueenIds.has(h.id) && !queenlessIds.has(h.id) && h.status !== 'ready');
                const others = allHivesList.filter(h => !queenlessIds.has(h.id) && h.status !== 'ready' && !splitNoQueenIds.has(h.id));
                
                const renderGroup = (label, emoji, color, groupHives) => {
                  if (groupHives.length === 0) return null;
                  return (
                    <div key={label}>
                      <div className={`text-xs font-bold uppercase tracking-wide px-2 py-1 ${color} rounded-t-lg`}>{emoji} {label} ({groupHives.length})</div>
                      <div className="grid grid-cols-2 gap-2 p-2 bg-stone-50 rounded-b-lg mb-2">
                        {groupHives.map(hive => (
                          <Button key={hive.id} variant="outline" className={`justify-start h-auto py-2.5 ${queenlessIds.has(hive.id) ? 'border-red-300 bg-red-50 hover:bg-red-100' : hive.status === 'ready' ? 'border-blue-300 bg-blue-50 hover:bg-blue-100' : splitNoQueenIds.has(hive.id) ? 'border-orange-300 bg-orange-50 hover:bg-orange-100' : ''}`}
                            onClick={() => handleMoveQueenToHive(hive.id)}
                          >
                            <div className="text-left">
                              <div className="font-semibold text-sm">Hive #{hive.hive_number}</div>
                              <div className="text-xs text-stone-500">{hive.yard || hive.location}</div>
                              {hive.is_split && <div className="text-xs text-orange-600">Split from #{hive.split_from_hive}</div>}
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                };
                
                return (
                  <>
                    {renderGroup('Queenless — Needs Queen', '👑', 'text-red-700 bg-red-100', queenless)}
                    {renderGroup('Ready Hives — Empty Equipment', '📦', 'text-blue-700 bg-blue-100', ready)}
                    {renderGroup('Recent Splits — May Need Queen', '✂️', 'text-orange-700 bg-orange-100', splits)}
                    {queenless.length === 0 && ready.length === 0 && splits.length === 0 && (
                      <div className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3 mb-2">No queenless or ready hives detected. Select any hive below:</div>
                    )}
                    {renderGroup('All Other Hives', '', 'text-stone-600 bg-stone-100', others)}
                  </>
                );
              })()}
            </div>
            
            <Button variant="outline" className="w-full" onClick={() => { setShowMoveToHiveDialog(false); setMovingNuc(null); }}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Transfer Dialog */}
      <Dialog open={showBulkTransferDialog} onOpenChange={(open) => {
        setShowBulkTransferDialog(open);
        if (!open) {
          setSelectedCellsForBulk([]); setBulkHiveMap({});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-indigo-600" />
              Quick Transfer - Capped Cells
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <TransferTargetToggle value={bulkTarget} onChange={setBulkTarget} testPrefix="bulk-transfer" />
            <p className="text-sm text-stone-600">
              {bulkTarget === 'hive'
                ? 'Select capped cells and choose a queenless hive or split for each one. Cells are marked "Transferred" and each hive is marked "cell introduced".'
                : 'Select capped cells and assign them to available mating NUCs. This will mark cells as "Transferred" and update NUC status to "Occupied".'}
            </p>
            
            {/* Available NUCs count */}
            {bulkTarget === 'nuc' && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-blue-800">
                <span className="font-semibold">{matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length}</span> NUCs available
              </span>
              <span className="text-sm text-indigo-800">
                <span className="font-semibold">{selectedCellsForBulk.length}</span> cells selected
              </span>
            </div>}
            
            {/* Capped cells grouped by batch */}
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {batches.map(batch => {
                const cappedCells = [];
                const totalCells = batch.cells_grafted || 0;
                
                // Get all capped cells (both existing and placeholder)
                for (let i = 1; i <= totalCells; i++) {
                  const existingCell = batch.cells?.find(c => c.cell_number === i);
                  if (existingCell?.status === 'capped') {
                    cappedCells.push({ ...existingCell, batchId: batch.id, batchName: batch.batch_name || `Batch #${batch.batch_number}` });
                  }
                }
                
                if (cappedCells.length === 0) return null;
                
                return (
                  <div key={batch.id} className="border rounded-lg p-3">
                    <h4 className="font-medium text-stone-700 mb-2">{batch.batch_name || `Batch #${batch.batch_number}`}</h4>
                    <div className="flex flex-wrap gap-2">
                      {cappedCells.map(cell => {
                        const isSelected = selectedCellsForBulk.some(s => s.cellId === cell.id);
                        return (
                          <button
                            key={cell.id}
                            data-testid={`bulk-cell-chip-${cell.id}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCellsForBulk(prev => prev.filter(s => s.cellId !== cell.id));
                              } else {
                                setSelectedCellsForBulk(prev => [...prev, { 
                                  cellId: cell.id, 
                                  cellNumber: cell.cell_number, 
                                  batchId: cell.batchId,
                                  batchName: cell.batchName
                                }]);
                                if (alertHiveId && !Object.values(bulkHiveMap).includes(alertHiveId)) {
                                  setBulkHiveMap(prev => ({ ...prev, [cell.id]: alertHiveId }));
                                }
                              }
                            }}
                            className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                              isSelected 
                                ? 'bg-indigo-100 border-indigo-400 text-indigo-800' 
                                : 'bg-purple-50 border-purple-200 text-purple-800 hover:border-indigo-300'
                            }`}
                          >
                            #{cell.cell_number}
                            {isSelected && <CheckCircle2 className="inline h-3 w-3 ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Transfer actions */}
            {selectedCellsForBulk.length > 0 && bulkTarget === 'hive' && (
              <div className="border-t pt-4 space-y-3" data-testid="bulk-hive-assign">
                <h4 className="font-medium text-stone-700">Choose a hive for each cell:</h4>
                {bulkCandidates === null ? <p className="text-sm text-stone-500">Loading hives…</p> : selectedCellsForBulk.map(cell => (
                  <div key={cell.cellId} className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-sm font-medium text-indigo-700 sm:w-40 shrink-0">{cell.batchName} · Cell #{cell.cellNumber}</span>
                    <select value={bulkHiveMap[cell.cellId] || ''} onChange={e => setBulkHiveMap(prev => ({ ...prev, [cell.cellId]: e.target.value }))}
                      className="flex-1 min-w-0 border border-stone-300 rounded-lg px-2 py-2 text-sm bg-white" data-testid={`bulk-hive-select-${cell.cellId}`}>
                      <option value="">Select a hive…</option>
                      <optgroup label="Best for a queen cell">
                        {bulkCandidates.filter(h => h.viable).map(h => (
                          <option key={h.id} value={h.id} disabled={Object.entries(bulkHiveMap).some(([cid, hid]) => hid === h.id && cid !== cell.cellId)}>{candidateLabel(h)}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Other hives">
                        {bulkCandidates.filter(h => !h.viable).map(h => (
                          <option key={h.id} value={h.id} disabled={Object.entries(bulkHiveMap).some(([cid, hid]) => hid === h.id && cid !== cell.cellId)}>{candidateLabel(h)}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                ))}
                <p className="text-xs text-stone-500">Queenless is taken from each hive's latest inspection. Queen check reminders are added for 10 and 21 days later.</p>
                <Button data-testid="bulk-hive-transfer-submit" className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={transferBusy || !selectedCellsForBulk.some(c => bulkHiveMap[c.cellId])}
                  onClick={async () => {
                    setTransferBusy(true);
                    let ok = 0;
                    for (const cell of selectedCellsForBulk.filter(c => bulkHiveMap[c.cellId])) {
                      const res = await fetch(`${API}/api/queen-rearing/batches/${cell.batchId}/cells/${cell.cellId}/to-hive`, {
                        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ hive_id: bulkHiveMap[cell.cellId], cell_number: cell.cellNumber })
                      }).catch(() => null);
                      if (res?.ok) ok += 1;
                    }
                    setTransferBusy(false);
                    const chosen = selectedCellsForBulk.filter(c => bulkHiveMap[c.cellId]).length;
                    if (ok === chosen) toast.success(`${ok} cell${ok === 1 ? '' : 's'} placed in hives`, { description: 'Queen check reminders added to Tasks and Calendar.' });
                    else if (ok > 0) toast.warning(`${ok} of ${chosen} cells placed in hives`, { description: 'Some could not be saved. Please check and try those again.' });
                    else { toast.error('No cells could be placed. Please try again.'); return; }
                    setShowBulkTransferDialog(false);
                    setSelectedCellsForBulk([]);
                    setBulkHiveMap({});
                    fetchData();
                    window.dispatchEvent(new Event('refreshData'));
                  }}>
                  Put {selectedCellsForBulk.filter(c => bulkHiveMap[c.cellId]).length} cell(s) into hives
                </Button>
              </div>
            )}
            {selectedCellsForBulk.length > 0 && bulkTarget === 'nuc' && (
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-medium text-stone-700">Assign to NUCs:</h4>
                <div className="flex flex-wrap gap-2">
                  {matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').slice(0, selectedCellsForBulk.length).map((nuc, idx) => (
                    <div key={nuc.id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                      <span className="font-medium text-green-800">NUC #{nuc.nuc_number}</span>
                      <span className="text-green-600">←</span>
                      <span className="text-indigo-700">Cell #{selectedCellsForBulk[idx]?.cellNumber}</span>
                    </div>
                  ))}
                </div>
                
                {selectedCellsForBulk.length > matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length && (
                  <p className="text-sm text-orange-600">
                    ⚠️ {selectedCellsForBulk.length - matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length} cells won't be assigned (not enough NUCs)
                  </p>
                )}
                
                <Button 
                  data-testid="bulk-transfer-submit"
                  disabled={Math.min(selectedCellsForBulk.length, matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length) === 0}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={async () => {
                    const availableNucs = matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees');
                    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
                    let successCount = 0;
                    
                    for (let i = 0; i < Math.min(selectedCellsForBulk.length, availableNucs.length); i++) {
                      const cell = selectedCellsForBulk[i];
                      const nuc = availableNucs[i];
                      
                      try {
                        // Update cell status
                        await fetch(`${API}/api/queen-rearing/batches/${cell.batchId}/cells/${cell.cellId}`, {
                          method: 'PUT',
                          headers,
                          body: JSON.stringify({ status: 'transferred', nuc_id: nuc.id })
                        });
                        
                        // Update NUC
                        await fetch(`${API}/api/queen-rearing/mating-nucs/${nuc.id}`, {
                          method: 'PUT',
                          headers,
                          body: JSON.stringify({
                            status: 'occupied',
                            current_cell_id: cell.cellId,
                            current_batch_id: cell.batchId,
                            current_cell_number: cell.cellNumber,
                            current_batch_name: cell.batchName,
                            queen_status: 'cell_transferred'
                          })
                        });
                        
                        successCount++;
                      } catch (error) {
                      }
                    }
                    
                    toast.success(`${successCount} cells transferred to NUCs`);
                    setShowBulkTransferDialog(false);
                    setSelectedCellsForBulk([]);
                    fetchData();
                  }}
                >
                  Transfer {Math.min(selectedCellsForBulk.length, matingNucs.filter(n => n.status === 'available' || n.status === 'needs-bees').length)} Cells
                </Button>
              </div>
            )}
            
            <Button variant="outline" className="w-full" onClick={() => { setShowBulkTransferDialog(false); setSelectedCellsForBulk([]); }}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

// Batch Timeline Card Component (for Calendar tab)
const BatchTimelineCard = ({ batch, formatDate, getDaysUntil, actions }) => {
  const [expanded, setExpanded] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  
  // Calculate timeline dates from graft date
  const getTimelineDates = (graftDate) => {
    if (!graftDate) return null;
    const graft = new Date(graftDate);
    return {
      graft: { date: graft, label: 'Graft', color: 'blue' },
      capped: { date: new Date(graft.getTime() + 9 * 24 * 60 * 60 * 1000), label: 'Capped', color: 'purple' },
      transfer: { date: new Date(graft.getTime() + 10 * 24 * 60 * 60 * 1000), label: 'Transfer', color: 'indigo' },
      emerge: { date: new Date(graft.getTime() + 16 * 24 * 60 * 60 * 1000), label: 'Emerge', color: 'pink' },
      mating: { date: new Date(graft.getTime() + 22 * 24 * 60 * 60 * 1000), label: 'Mating', color: 'orange' },
      check: { date: new Date(graft.getTime() + 30 * 24 * 60 * 60 * 1000), label: 'Check', color: 'green' }
    };
  };
  
  const timeline = getTimelineDates(batch.graft_date);
  if (!timeline) return null;
  const currentStage = Object.keys(timeline).find(k => getDaysUntil(timeline[k].date) >= 0) || 'check';
  const activeStage = selectedStage || currentStage;
  
  const colorMap = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500',
    pink: 'bg-pink-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500'
  };
  
  const bgColorMap = {
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
    indigo: 'bg-indigo-50 border-indigo-200',
    pink: 'bg-pink-50 border-pink-200',
    orange: 'bg-orange-50 border-orange-200',
    green: 'bg-green-50 border-green-200'
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded); }}
        className="w-full px-4 py-3 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors border-b"
      >
        <div className="flex items-center gap-3">
          <Crown className="h-5 w-5 text-amber-600" />
          <div className="text-left">
            <h3 className="font-semibold text-stone-800">{batch.batch_name || `Batch #${batch.batch_number}`}</h3>
            <p className="text-sm text-stone-500">Grafted: {formatDate(batch.graft_date)} • {batch.cells_grafted} cells</p>
            <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
              {batch.yard_name && <span data-testid={`timeline-apiary-${batch.id}`} className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">📍 {batch.yard_name}</span>}
              {batch.breeder_queen_name && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">👑 {batch.breeder_queen_name}</span>}
              {batch.cell_builder_hive_number && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Starter #{batch.cell_builder_hive_number}</span>}
              {batch.finisher_hive_number && <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">Finisher #{batch.finisher_hive_number}</span>}
            </div>
          </div>
        </div>
        <div className={`p-1 rounded-full ${expanded ? 'bg-amber-200' : 'bg-stone-100'}`}>
          {expanded ? <ChevronUp className="h-5 w-5 text-amber-700" /> : <ChevronDown className="h-5 w-5 text-stone-400" />}
        </div>
      </button>
      
      {expanded && (
        <CardContent className="pt-4">
          <div className="flex justify-end mb-2">
            <Button data-testid={`timeline-calendar-btn-${batch.id}`} size="sm" variant="outline" onClick={() => printGraftCalendar([batch])} className="h-8 text-xs border-amber-300 text-amber-700">
              <CalendarDays className="h-3.5 w-3.5 mr-1" /> Print calendar
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            {Object.entries(timeline).map(([key, event]) => {
              const days = getDaysUntil(event.date);
              const isPast = days < 0;
              const isToday = days === 0;
              const isSoon = days > 0 && days <= 3;
              
              return (
                <button
                  type="button"
                  key={key}
                  data-testid={`timeline-stage-${batch.id}-${key}`}
                  onClick={() => setSelectedStage(key)}
                  className={`p-3 rounded-lg border text-center transition-transform hover:-translate-y-0.5 ${activeStage === key ? 'outline outline-2 outline-offset-2 outline-sky-500 shadow-md' : ''} ${
                    isToday ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-400' :
                    isPast ? 'bg-gray-50 border-gray-200 opacity-60' :
                    isSoon ? bgColorMap[event.color] :
                    'bg-white border-stone-200'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full mx-auto mb-2 ${colorMap[event.color]}`} />
                  <div className="font-medium text-sm text-stone-800">{event.label}</div>
                  <div className="text-xs text-stone-500">{formatDate(event.date)}</div>
                  <div className={`text-xs mt-1 font-medium ${
                    isToday ? 'text-amber-700' :
                    isPast ? 'text-gray-500' :
                    isSoon ? 'text-orange-600' :
                    'text-stone-500'
                  }`}>
                    {isPast ? 'Done' : isToday ? 'TODAY!' : `${days}d`}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-stone-400 mt-2">Tap a stage to see tips for it</p>
          <QueenStageHints stageKey={activeStage} daysUntil={getDaysUntil(timeline[activeStage].date)} />
          {actions && <StageActions batch={batch} stageKey={activeStage} {...actions} />}
        </CardContent>
      )}
    </Card>
  );
};

// Batch Card Component
const BatchCard = ({ batch, focused, onFocusDone, onEdit, onDelete, onArchive, onUnarchive, onUpdateCell, onAssignQueen, formatDate, getStatusColor, getDaysUntil }) => {
  const [expanded, setExpanded] = useState(false);
  const cardRef = React.useRef(null);
  useEffect(() => {
    if (!focused) return;
    setExpanded(true);
    setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    const t = setTimeout(() => onFocusDone?.(), 3000);
    return () => clearTimeout(t);
  }, [focused]);
  
  // Export single batch to PDF
  const exportBatchToPDF = () => {
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const pageWidth = 210;
      const marginLeft = 14;
      const contentWidth = 182;
      
      // Helper function to capitalize first letter of each word
      const toTitleCase = (str) => {
        if (!str) return '-';
        return str.replace(/\b\w/g, l => l.toUpperCase());
      };
      
      // Helper to calculate timeline dates from graft date
      const getTimelineDates = (graftDate) => {
        if (!graftDate) return null;
        const graft = new Date(graftDate);
        return {
          graft: graft,
          capped: new Date(graft.getTime() + 9 * 24 * 60 * 60 * 1000),
          transfer: new Date(graft.getTime() + 10 * 24 * 60 * 60 * 1000),
          emerge: new Date(graft.getTime() + 16 * 24 * 60 * 60 * 1000),
          mating: new Date(graft.getTime() + 22 * 24 * 60 * 60 * 1000),
          check: new Date(graft.getTime() + 30 * 24 * 60 * 60 * 1000)
        };
      };
      
      // Short date format
      const shortDate = (date) => {
        if (!date) return '-';
        if (typeof date === 'string') date = new Date(date);
        return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      };
      
      // Header
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 83, 9);
      doc.text('Batch Report', marginLeft, yPos);
      yPos += 12;
      
      // Date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} at ${new Date().toLocaleTimeString()}`, marginLeft, yPos);
      yPos += 15;
      
      // Batch Name Header
      doc.setFillColor(180, 83, 9);
      doc.roundedRect(marginLeft, yPos, contentWidth, 14, 2, 2, 'F');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255);
      doc.text(batch.batch_name || `Batch #${batch.batch_number}`, marginLeft + 6, yPos + 10);
      yPos += 22;
      
      // Batch Details Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60);
      doc.text('Batch Details', marginLeft, yPos);
      yPos += 8;
      
      // Details table
      const details = [
        ['Status', toTitleCase(batch.status)],
        ['Apiary', batch.yard_name || '-'],
        ['Breeder Queen', batch.breeder_queen_name || '-'],
        ['Cell Starter Hive', batch.cell_builder_hive_number ? `Hive #${batch.cell_builder_hive_number}` : '-'],
        ['Finisher Hive', batch.finisher_hive_number ? `Hive #${batch.finisher_hive_number}` : 'Same as starter'],
        ['Cells Grafted', String(batch.cells_grafted || 0)],
        ['Capped / Emerged', `${batch.cells_capped || 0} / ${batch.queens_emerged || 0}`],
        ['Mated / Laying', `${batch.queens_mated || 0} / ${batch.queens_laying || 0}`],
        ['Graft Date', shortDate(batch.graft_date)],
        ['Grafting Tool', toTitleCase(batch.grafting_tool)],
        ['Cell Cups', toTitleCase(batch.cell_cups)],
        ['Notes', batch.notes || '-']
      ];
      
      details.forEach((row, idx) => {
        if (idx % 2 === 0) {
          doc.setFillColor(255, 251, 235);
          doc.rect(marginLeft, yPos - 1, contentWidth, 8, 'F');
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text(row[0] + ':', marginLeft + 4, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40);
        doc.text(String(row[1]).substring(0, 80), marginLeft + 50, yPos + 5);
        yPos += 8;
      });
      yPos += 10;
      
      // Stats Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60);
      doc.text('Cell Statistics', marginLeft, yPos);
      yPos += 8;
      
      // Stats boxes - match the UI batch card: Grafted → Accepted → Capped → Transferred
      const stats = [
        { label: 'Grafted', value: batch.cells_grafted || 0, color: [219, 234, 254] },
        { label: 'Accepted', value: batch.cells_accepted || 0, color: [220, 252, 231] },
        { label: 'Capped', value: batch.cells_capped || 0, color: [243, 232, 255] },
        { label: 'Transferred', value: (batch.cells || []).filter(c => c.status === 'transferred').length || 0, color: [224, 231, 255] }
      ];
      
      stats.forEach((stat, i) => {
        const x = marginLeft + (i * 46);
        doc.setFillColor(...stat.color);
        doc.roundedRect(x, yPos, 44, 22, 2, 2, 'F');
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text(String(stat.value), x + 22, yPos + 12, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.text(stat.label, x + 22, yPos + 18, { align: 'center' });
      });
      yPos += 32;
      
      // Timeline Section
      const timeline = getTimelineDates(batch.graft_date);
      if (timeline) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text('Timeline', marginLeft, yPos);
        yPos += 8;
        
        // Timeline header row
        doc.setFillColor(254, 243, 199);
        doc.rect(marginLeft, yPos, contentWidth, 10, 'F');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 53, 0);
        
        const colWidth = contentWidth / 6;
        const milestones = ['Graft', 'Capped', 'Transfer', 'Emerge', 'Mating', 'Check'];
        milestones.forEach((label, idx) => {
          doc.text(label, marginLeft + (idx * colWidth) + colWidth/2, yPos + 7, { align: 'center' });
        });
        yPos += 10;
        
        // Timeline date row
        doc.setFillColor(255, 251, 235);
        doc.rect(marginLeft, yPos, contentWidth, 10, 'F');
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.setFontSize(8);
        
        const dates = [
          shortDate(timeline.graft),
          shortDate(timeline.capped),
          shortDate(timeline.transfer),
          shortDate(timeline.emerge),
          shortDate(timeline.mating),
          shortDate(timeline.check)
        ];
        dates.forEach((dateStr, idx) => {
          doc.text(dateStr, marginLeft + (idx * colWidth) + colWidth/2, yPos + 7, { align: 'center' });
        });
        yPos += 18;
      }
      
      // Individual Cells Section
      if (batch.cells && batch.cells.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text(`Individual Cells (${batch.cells.length})`, marginLeft, yPos);
        yPos += 8;
        
        // Cells table header
        doc.setFillColor(180, 83, 9);
        doc.rect(marginLeft, yPos, contentWidth, 8, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Cell #', marginLeft + 4, yPos + 5.5);
        doc.text('Status', marginLeft + 30, yPos + 5.5);
        doc.text('Mating Nuc', marginLeft + 80, yPos + 5.5);
        doc.text('Assigned Hive', marginLeft + 130, yPos + 5.5);
        yPos += 8;
        
        // Cells rows
        doc.setFont('helvetica', 'normal');
        batch.cells.forEach((cell, idx) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          if (idx % 2 === 0) {
            doc.setFillColor(255, 251, 235);
            doc.rect(marginLeft, yPos, contentWidth, 7, 'F');
          }
          
          doc.setTextColor(40);
          doc.setFontSize(8);
          doc.text(`#${cell.cell_number}`, marginLeft + 4, yPos + 5);
          doc.text(toTitleCase(cell.status), marginLeft + 30, yPos + 5);
          doc.text(cell.mating_nuc_id || '-', marginLeft + 80, yPos + 5);
          doc.text(cell.assigned_hive_id ? `Hive ${cell.assigned_hive_number || cell.assigned_hive_id}` : '-', marginLeft + 130, yPos + 5);
          yPos += 7;
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150);
        doc.text(`Batch Report: ${batch.batch_name || 'Batch #' + batch.batch_number} - Page ${i} of ${pageCount}`, pageWidth / 2, 287, { align: 'center' });
      }
      
      doc.save(`batch-${(batch.batch_name || 'batch-' + batch.batch_number).toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Batch PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export batch PDF');
    }
  };
  
  return (
    <Card ref={cardRef} data-testid={`batch-card-${batch.id}`} className={`border-amber-200 scroll-mt-4 transition-shadow ${focused ? 'ring-4 ring-sky-400 shadow-lg' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-600" />
              {batch.batch_name || `Batch #${batch.batch_number}`}
            </h3>
            <div className="flex gap-2 mt-1 flex-wrap items-center">
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(batch.status)}`}>
                {batch.status}
              </span>
              {batch.graft_date && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                  📅 Grafted: {formatDate(batch.graft_date)}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={exportBatchToPDF} title="Export Batch PDF">
              <FileText className="h-4 w-4 text-amber-600" />
            </Button>
            <Button data-testid={`batch-calendar-btn-${batch.id}`} variant="ghost" size="icon" onClick={() => printGraftCalendar([batch])} title="Print graft calendar">
              <CalendarDays className="h-4 w-4 text-amber-600" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEdit}>
              <Edit2 className="h-4 w-4" />
            </Button>
            {batch.status === 'archived' ? (
              <Button variant="ghost" size="icon" className="text-green-600" onClick={onUnarchive} title="Restore batch">
                <RotateCcw className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon" className="text-stone-400" onClick={onArchive} title="Archive batch">
                <Archive className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="text-red-500" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Key Info Row */}
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          {batch.yard_name && (
            <span data-testid={`batch-apiary-${batch.id}`} className="bg-teal-50 text-teal-700 px-2 py-1 rounded flex items-center gap-1">
              📍 <span className="font-medium">{batch.yard_name}</span>
            </span>
          )}
          {batch.breeder_queen_name && (
            <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
              👑 <span className="font-medium">{batch.breeder_queen_name}</span>
            </span>
          )}
          {batch.cell_builder_hive_number && (
            <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded flex items-center gap-1" title="Queenless hive for initial cell building">
              🏠 Starter: <span className="font-medium">#{batch.cell_builder_hive_number}</span>
            </span>
          )}
          {batch.finisher_hive_number && batch.finisher_hive_number !== batch.cell_builder_hive_number && (
            <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded flex items-center gap-1" title="Queenright hive with excluder">
              🏠 Finisher: <span className="font-medium">#{batch.finisher_hive_number}</span>
            </span>
          )}
          {batch.cells_per_frame && (
            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
              📐 <span className="font-medium">{batch.cells_per_frame}</span>/frame
            </span>
          )}
          {batch.incubator_used && (
            <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded flex items-center gap-1">
              🌡️ Incubator
            </span>
          )}
        </div>

        {/* Timeline - Batch stages (before transfer to NUCs) */}
        <div className="bg-amber-50 rounded-lg p-3 mb-3">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Cell Timeline
          </h4>
          <div className="grid grid-cols-4 gap-1 text-xs">
            {[
              { label: 'Graft', date: batch.graft_date, icon: '🔬', desc: 'Day 0' },
              { label: 'Finisher', date: batch.move_to_finisher_date, icon: '🏠', desc: 'Day 1' },
              { label: 'Capped', date: batch.graft_date ? new Date(new Date(batch.graft_date).getTime() + 9 * 86400000).toISOString().slice(0, 10) : batch.cells_capped_date, icon: '📦', desc: 'Day 9' },
              { label: 'Transfer', date: batch.transfer_to_nucs_date, icon: '➡️', desc: 'Day 10' }
            ].map(item => {
              const days = getDaysUntil(item.date);
              const isPast = days !== null && days < 0;
              const isToday = days === 0;
              const isSoon = days !== null && days > 0 && days <= 3;
              
              return (
                <div key={item.label} className={`text-center p-2 rounded ${
                  isPast ? 'bg-gray-100' : isToday ? 'bg-amber-200' : isSoon ? 'bg-orange-100' : 'bg-white'
                }`}>
                  <div>{item.icon}</div>
                  <div className="font-medium">{item.label}</div>
                  <div className="text-stone-500">{formatDate(item.date)}</div>
                  {days !== null && !isPast && (
                    <div className={`text-xs ${isToday ? 'text-amber-700 font-bold' : isSoon ? 'text-orange-600' : ''}`}>
                      {isToday ? 'Today!' : `${days}d`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats - Cell journey through batch */}
        <div className="grid grid-cols-4 gap-2 mb-3 text-center text-sm">
          <div className="bg-blue-50 rounded p-2">
            <div className="font-bold text-blue-800">{batch.cells_grafted || 0}</div>
            <div className="text-[10px] text-blue-600">Grafted</div>
          </div>
          <div className="bg-green-50 rounded p-2">
            <div className="font-bold text-green-800">{batch.cells_accepted || 0}</div>
            <div className="text-[10px] text-green-600">Accepted</div>
          </div>
          <div className="bg-purple-50 rounded p-2">
            <div className="font-bold text-purple-800">{batch.cells_capped || 0}</div>
            <div className="text-[10px] text-purple-600">Capped</div>
          </div>
          <div className="bg-indigo-50 rounded p-2">
            <div className="font-bold text-indigo-800">{batch.cells?.filter(c => c.status === 'transferred').length || 0}</div>
            <div className="text-[10px] text-indigo-600">Transferred</div>
          </div>
        </div>

        {/* Transfer Rate Bar */}
        {batch.cells_grafted > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>Transfer Rate</span>
              <span>{(() => {
                const moved = batch.cells?.filter(c => c.status === 'transferred') || [];
                const toHives = moved.filter(c => c.transferred_to === 'hive').length;
                const pct = Math.round((moved.length / batch.cells_grafted) * 100);
                return `${pct}% transferred${toHives ? ` (${moved.length - toHives} to NUCs, ${toHives} to hives)` : ' to NUCs'}`;
              })()}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-500 transition-all"
                style={{ width: `${Math.min(100, ((batch.cells?.filter(c => c.status === 'transferred').length || 0) / batch.cells_grafted) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Notes preview if exists */}
        {batch.notes && (
          <div className="text-xs text-stone-500 bg-stone-50 rounded p-2 mb-3 italic">
            📝 {batch.notes.substring(0, 100)}{batch.notes.length > 100 ? '...' : ''}
          </div>
        )}

        {/* Expand/Collapse Cells */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
          {expanded ? 'Hide' : 'Show'} Individual Cells ({batch.cells_grafted || batch.cells?.length || 0})
        </Button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Option B: Visual Frame Layout */}
            {(() => {
              const cellsPerFrame = batch.cells_per_frame || 15;
              const totalCells = batch.cells_grafted || batch.cells?.length || 0;
              const numFrames = Math.ceil(totalCells / cellsPerFrame);
              
              return Array.from({ length: numFrames }, (_, frameIdx) => {
                const frameNumber = frameIdx + 1;
                const startCell = frameIdx * cellsPerFrame + 1;
                const endCell = Math.min((frameIdx + 1) * cellsPerFrame, totalCells);
                const cellsInFrame = endCell - startCell + 1;
                
                // Get cells for this frame
                const frameCells = Array.from({ length: cellsInFrame }, (_, cellIdx) => {
                  const cellNumber = startCell + cellIdx;
                  const existingCell = batch.cells?.find(c => c.cell_number === cellNumber);
                  return existingCell || { 
                    id: `placeholder-${cellNumber}`, 
                    cell_number: cellNumber, 
                    status: 'grafted' 
                  };
                });
                
                // Calculate frame stats
                const frameStats = {
                  grafted: frameCells.filter(c => c.status === 'grafted').length,
                  accepted: frameCells.filter(c => c.status === 'accepted').length,
                  capped: frameCells.filter(c => c.status === 'capped').length,
                  failed: frameCells.filter(c => c.status === 'failed').length,
                };
                
                return (
                  <div key={frameNumber} className="bg-amber-50 border-2 border-amber-300 rounded-xl overflow-hidden">
                    {/* Frame Header */}
                    <div className="bg-amber-100 px-4 py-2 flex items-center justify-between border-b border-amber-200">
                      <div className="flex items-center gap-2">
                        <div className="bg-amber-600 text-white px-2 py-1 rounded text-sm font-bold">
                          Frame {frameNumber}
                        </div>
                        <span className="text-sm text-amber-800">
                          Cells #{startCell} - #{endCell}
                        </span>
                      </div>
                      <div className="flex gap-2 text-xs">
                        {frameStats.accepted > 0 && (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{frameStats.accepted} accepted</span>
                        )}
                        {frameStats.capped > 0 && (
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{frameStats.capped} capped</span>
                        )}
                        {frameStats.failed > 0 && (
                          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">{frameStats.failed} failed</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Frame Cells - Single row with horizontal scroll */}
                    <div className="p-4 overflow-x-auto">
                      <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
                        {frameCells.map(cell => (
                          <div
                            key={cell.id}
                            className={`relative group w-20 h-24 rounded-xl border-2 flex flex-col items-center justify-center text-xs cursor-pointer transition-all hover:scale-105 hover:shadow-lg flex-shrink-0 ${
                              cell.status === 'grafted' ? 'bg-blue-50 border-blue-200' :
                              cell.status === 'accepted' ? 'bg-green-50 border-green-300' :
                              cell.status === 'capped' ? 'bg-purple-100 border-purple-300' :
                              cell.status === 'transferred' ? 'bg-indigo-100 border-indigo-300' :
                              cell.status === 'emerged' ? 'bg-pink-100 border-pink-300' :
                              cell.status === 'mated' ? 'bg-orange-100 border-orange-300' :
                              cell.status === 'laying' ? 'bg-amber-100 border-amber-400' :
                              cell.status === 'failed' ? 'bg-red-100 border-red-300 opacity-60' :
                              'bg-gray-50 border-gray-200'
                            }`}
                            title={`Cell ${cell.cell_number}: ${cell.status}${cell.nuc_number ? ` - NUC #${cell.nuc_number}` : ''}${cell.assigned_hive_number ? ` - Hive #${cell.assigned_hive_number}` : ''}`}
                          >
                            <div className="font-bold text-stone-700 text-sm">#{cell.cell_number}</div>
                            <div className="text-[11px] text-stone-600 font-medium capitalize">{cell.status}</div>
                            
                            {/* Show NUC # for transferred, mated, or laying cells */}
                            {['transferred', 'mated', 'laying'].includes(cell.status) && cell.nuc_number && (
                              <div className="text-[10px] text-indigo-600 font-semibold mt-0.5">
                                NUC #{cell.nuc_number}
                              </div>
                            )}
                            
                            {/* Show Hive # for cells assigned to a hive (after laying queen is installed) */}
                            {cell.assigned_hive_number && (
                              <div className="text-[10px] text-orange-600 font-semibold mt-0.5">
                                Hive #{cell.assigned_hive_number}
                              </div>
                            )}
                            
                            {/* Dropdown on hover/click - Only cell-stage statuses */}
                            <select
                              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                              value={cell.status}
                              onChange={(e) => onUpdateCell(batch.id, cell.id.startsWith('placeholder') ? `new-${cell.cell_number}` : cell.id, e.target.value, cell.cell_number)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="grafted">Grafted</option>
                              <option value="accepted">Accepted</option>
                              <option value="capped">Capped</option>
                              <option value="transferred">Transferred</option>
                              <option value="failed">Failed</option>
                            </select>
                            
                            {/* Note: emerged/mated/laying tracking moved to Mating NUCs tab */}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QueenRearing;
